import { useState, useEffect } from "react";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  useMaterialReactTable,
  MRT_RowData,
  MRT_PaginationState,
  MRT_Cell,
  MRT_Row,
  MRT_TableInstance,
} from "material-react-table";
import { OnChangeFn } from "@tanstack/table-core";
import { Updater } from "@tanstack/table-core/build/lib/types";
import { useNavigate } from "react-router-dom";
import GeneralAlert from "./common/GeneralAlert";
import useVisibleColumns from "../hooks/useVisibleColumns";

interface EntityTableProps<T extends MRT_RowData> {
  loading?: boolean;
  error?: string | null;
  columns: MRT_ColumnDef<T>[];
  /**
   * Ids of columns to drop before rendering, for tables anchored to a single
   * entity (an entity page's connection / filtered sections) where a column
   * repeats that entity on every row.
   *
   * This is the subtractive half of column selection: which of an entity's
   * columns are pointless *here*. Which columns an entity has in the first
   * place stays additive and belongs in the column sets and `hooks/logic`
   * (`languageBasedColumns`, `getPersonVersionsColumns`, …) — if a column is
   * redundant for every possible caller, remove it there instead.
   */
  hiddenColumnIds?: Extract<keyof T, string>[];
  data: T[];
  pagination: MRT_PaginationState & { sort?: string }; // Add sort to pagination state
  manualPagination: boolean;
  onPaginationChange: (
      pagination: Updater<{
        pageIndex: number;
        pageSize: number;
        sort?: string;
      }>
  ) => void;
  enableRowSelection?: boolean;
  isMultipleRowSelection?: boolean;
  onRowSelectionChange?: (
      selectedData: T | null,
      selectedRowIndex: number | null
  ) => void;
  muiTableContainerProps?: object;
  totalRowCount: number;
  manualFiltering?: boolean;
  columnFilters?: MRT_ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<MRT_ColumnFiltersState>;
  initialSelectedEntityKey?: string;
  enableRowActions?: boolean;
  renderRowActions?: (props: {
    cell: MRT_Cell<T, unknown>;
    row: MRT_Row<T>;
    staticRowIndex?: number;
    table: MRT_TableInstance<T>;
  }) => React.ReactNode;
}

export type EntityTableFilterProps = {
  columnFilters?: MRT_ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<MRT_ColumnFiltersState>;
};

const EntityTable = <T extends MRT_RowData>({
                                              loading = false,
                                              error,
                                              columns,
                                              hiddenColumnIds,
                                              data,
                                              pagination,
                                              manualPagination,
                                              onPaginationChange,
                                              enableRowSelection = false,
                                              isMultipleRowSelection = false,
                                              onRowSelectionChange,
                                              muiTableContainerProps,
                                              totalRowCount,
                                              manualFiltering,
                                              columnFilters,
                                              onColumnFiltersChange,
                                              initialSelectedEntityKey,
                                              enableRowActions,
                                              renderRowActions,
                                            }: EntityTableProps<T>) => {
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const navigate = useNavigate();

  const visibleColumns = useVisibleColumns(columns, hiddenColumnIds);

  useEffect(() => {
    if (initialSelectedEntityKey) {
      const initialSelectedIndex = data.findIndex(
          (item) => item._key === initialSelectedEntityKey
      );

      if (initialSelectedIndex !== -1) {
        setRowSelection({ [initialSelectedIndex]: true });
      }
    }
  }, [initialSelectedEntityKey, data]);

  const table = useMaterialReactTable({
    columns: visibleColumns,
    data,
    manualFiltering: manualFiltering ?? false,
    enableGlobalFilter: false,
    enableColumnFilters: true,
    onColumnFiltersChange,
    enableRowSelection,
    enableMultiRowSelection: isMultipleRowSelection,
    // Filter-driven pagination reset is handled by useServerFilters.
    autoResetPageIndex: false,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      sx: { cursor: "pointer" },
    }),
    onRowSelectionChange: setRowSelection,
    onPaginationChange,
    state: {
      pagination,
      rowSelection,
      ...(columnFilters !== undefined && { columnFilters }),
      isLoading: loading,
      showLoadingOverlay: loading,
      showProgressBars: loading,
    },
    manualPagination,
    muiPaginationProps: {
      rowsPerPageOptions: [10, 50, 100, 500, 1000, 5000, 10000],
    },
    muiTableContainerProps,
    rowCount: totalRowCount,
    enableRowActions,
    renderRowActions,
  });

  useEffect(() => {
    if (!onRowSelectionChange) return;
    const selectedKey = Object.keys(rowSelection)[0];
    if (selectedKey === undefined) {
      onRowSelectionChange(null, null);
      return;
    }
    const selectedRowIndex = Number(selectedKey);
    const selectedData = data[selectedRowIndex] ?? null;
    onRowSelectionChange(selectedData, selectedData ? selectedRowIndex : null);
  }, [rowSelection, data, onRowSelectionChange]);

  if (error) {
    return (
        <GeneralAlert
            errorMessage={error}
            action={() => navigate(0)}
            actionLabel="Refresh Page"
        />
    );
  }

  return <MaterialReactTable table={table} />;
};

export default EntityTable;
